import React, { useState, useEffect, useRef } from 'react';
import { useSteps } from '../hooks/useSteps';
import { CompassNeedle } from './CompassNeedle';
import './StepTracker.css';
import bg from '../assets/schatzsuche1.jpg';
import kompassRose from '../assets/kompass-rose.png';
import kompassNadel from '../assets/kompass-nadel.png';
import { useSingletonAudio } from '../hooks/useSingletonAudio';
import schrittzielSound from '../assets/sounds/schrittziel.mp3';
import { useStepsContext } from "../hooks/StepsContext";
import { useXpBonus, XP_EVENTS } from "../hooks/useXpBonus";
import { useGamification } from '../hooks/GamificationContext';
import { usePlayerLocation } from '../game-map/PlayerLocationContext';
import {
  claimLocationLoot,
  createLootRequestId,
  getLootErrorMessage,
  loadLootLog as loadLootLogFromServer,
  LootApiError,
  waitForLootClaimResolution,
} from '../api/lootApi';

function distance(a, b) {
  const R = 6371000;
  const toRad = deg => (deg * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const a_ =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a_), Math.sqrt(1 - a_));
}

export function StepTracker({ rotation = 0 }) {
  const {
    dailySteps,
    sessionSteps,
    steps,
    totalSteps,
    streak,
    goalReachedToday,
    isTracking,
    startTracking,
    stopTracking,
    elapsed,
    DAILY_STEP_GOAL,
    motionPermission,
    trackingError,
    sensorReady,
    requiresMotionPermission,
  } = useSteps();

  const { steps: globalSteps } = useStepsContext();

  function getBboxAround(lat, lon, delta = 0.012) {
    return [
      (lat - delta).toFixed(6),
      (lon - delta).toFixed(6),
      (lat + delta).toFixed(6),
      (lon + delta).toFixed(6)
    ].join(",");
  }
  const percent = Math.min(100, Math.round((steps / DAILY_STEP_GOAL) * 100));
  const [dailyTotal, setDailyTotal] = useState(0);
  const [allTimeTotal, setAllTimeTotal] = useState(0);
  const userId = window.pfandpiratData.user_id;
  const [isHovernd, setIsHovernd] = useState(false);
  const [locations, setLocations] = useState([]);
  const playerLocation = usePlayerLocation();
  const coords = isTracking ? playerLocation.position : null;
  const [loot, setLoot] = useState(null);
  const { giveXP } = useXpBonus();
  const { applyGamificationPayload } = useGamification();
  const [lootLog, setLootLog] = useState([]);
  const [lootError, setLootError] = useState('');
  const LOOT_COOLDOWN = 30 * 60 * 1000; // 30 Minuten
  const [isLooting, setIsLooting] = useState(false);
  const lootClaimLockRef = useRef(false);
  const lootClaimUnlockTimerRef = useRef(null);

  const [cooldownId, setCooldownId] = useState(null);
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [cooldownTick, setCooldownTick] = useState(0);

  const [bbox, setBbox] = useState(null);
  const LOOT_RADIUS = 20;
  const playSchrittzielSound = useSingletonAudio(schrittzielSound, 0.35);

  const allLocations = locations;

  useEffect(() => () => {
    if (lootClaimUnlockTimerRef.current) {
      clearTimeout(lootClaimUnlockTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!coords) return;
    setBbox(getBboxAround(coords.latitude, coords.longitude, 0.012));
  }, [coords]);

  useEffect(() => {
    fetch("/wp-json/pfandpirat/v1/locations", { credentials: 'include' })
      .then(res => res.json())
      .then(data => setLocations(data));
  }, []);

  // Prüfen, ob ein Ort in der Nähe ist
  const [nearbyLocation, setNearbyLocation] = useState(null);

  useEffect(() => {
    if (!coords || !allLocations.length) {
      setIsHovernd(false);
      setNearbyLocation(null);
      return;
    }
    const lootable = allLocations
      .map(ort => ({ ...ort, dist: distance(coords, ort) }))
      .filter(ort => ort.dist < LOOT_RADIUS);
    const nearest = lootable.length
      ? lootable.reduce((a, b) => (a.dist < b.dist ? a : b))
      : null;
    setIsHovernd(!!nearest);
    setNearbyLocation(nearest);
  }, [coords, allLocations]);

  // --- Das Lootlog IMMER frisch laden, wenn der Benutzer, die Seite neu betritt ---
  useEffect(() => {
    if (!userId) return;
    loadLootLogFromServer()
      .then(setLootLog)
      .catch(error => {
        setLootLog([]);
        setLootError(getLootErrorMessage(error));
      });
  }, [userId]);

  useEffect(() => {
    const playedKey = 'pfandpirat_schrittziel_' + new Date().toISOString().slice(0, 10);
    if (dailySteps >= 5000 && !localStorage.getItem(playedKey)) {
      playSchrittzielSound();
      localStorage.setItem(playedKey, '1');
      giveXP(XP_EVENTS.STEPS_GOAL);
    }
  }, [dailySteps, giveXP]);

  // Cooldown-Logik
  function getCooldownTime(locationId) {
    const log = lootLog.find(l => l.location_id == locationId);
    if (!log) return 0;
    const elapsed = Date.now() - new Date(log.last_loot_at).getTime();
    return Math.max(0, LOOT_COOLDOWN - elapsed);
  }
  function formatCooldown(ms) {
    const min = Math.floor(ms / 60000);
    const sec = Math.floor((ms % 60000) / 1000);
    return `${min}:${sec.toString().padStart(2, '0')} min`;
  }
  function canLoot(locationId) {
    const log = lootLog.find(l => l.location_id == locationId);
    if (!log) return true;
    return getCooldownTime(locationId) <= 0;
  }

  // Popup-Timer für Live-Countdown
  useEffect(() => {
    if (!cooldownId || !cooldownUntil) return;
    const interval = setInterval(() => setCooldownTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [cooldownId, cooldownUntil]);

  // Cooldown vorbei → Lootlog aktualisieren
  useEffect(() => {
    if (!cooldownId || !cooldownUntil) return;
    if (Date.now() >= cooldownUntil) {
      // Sobald Cooldown vorbei: Lootlog aktualisieren!
      loadLootLogFromServer()
        .then(setLootLog)
        .catch(error => setLootError(getLootErrorMessage(error)));
      setCooldownId(null);
      setCooldownUntil(null);
    }
  }, [cooldownTick, cooldownId, cooldownUntil, userId]);

  function releaseLootClaimLock(delayMs = 0) {
    if (lootClaimUnlockTimerRef.current) {
      clearTimeout(lootClaimUnlockTimerRef.current);
    }
    if (delayMs <= 0) {
      lootClaimLockRef.current = false;
      lootClaimUnlockTimerRef.current = null;
      setIsLooting(false);
      return;
    }
    lootClaimUnlockTimerRef.current = setTimeout(() => {
      lootClaimLockRef.current = false;
      lootClaimUnlockTimerRef.current = null;
      setIsLooting(false);
    }, delayMs);
  }

  async function handleTruheClick(currentLocation) {
    if (lootClaimLockRef.current || isLooting) return;
    lootClaimLockRef.current = true;
    setIsLooting(true);
    setLootError('');

    if (!isHovernd || !currentLocation) {
      setLoot(null);
      setCooldownId(null);
      setCooldownUntil(null);
      releaseLootClaimLock();
      return;
    }

    const location = currentLocation;

    const userCoords = coords;
    if (!userCoords || !location.latitude || !location.longitude) {
      setLoot(null);
      setLootError('Deine aktuelle Position konnte nicht bestimmt werden.');
      releaseLootClaimLock();
      return;
    }
    const dist = distance(
      { latitude: parseFloat(location.latitude), longitude: parseFloat(location.longitude) },
      { latitude: userCoords.latitude, longitude: userCoords.longitude }
    );
    if (dist > LOOT_RADIUS) {
      setLoot(null);
      setCooldownId(null);
      setCooldownUntil(null);
      setLootError('Du bist zu weit entfernt, gehe näher ran!');
      releaseLootClaimLock();
      return;
    }

    const requestId = createLootRequestId();
    try {
      let claim;
      try {
        claim = await claimLocationLoot({
          locationId: location.id,
          latitude: userCoords.latitude,
          longitude: userCoords.longitude,
          accuracy: userCoords.accuracy,
          positionTimestamp: userCoords.receivedAt || userCoords.timestamp,
          requestId,
        });
      } catch (error) {
        if (error?.code !== 'loot_request_timeout') throw error;
        setLootError('Die Verbindung ist langsam. Wir prüfen, ob deine Beute verbucht wurde.');
        claim = await waitForLootClaimResolution(requestId);
        if (!claim) {
          throw new LootApiError('Der Loot-Status ist noch nicht eindeutig.', {
            code: 'loot_claim_status_pending',
          });
        }
      }
      const lootItem = claim?.item;
      if (!lootItem?.name) {
        throw new Error('Der Server hat keinen Lootgegenstand zurückgegeben.');
      }

      if (claim?.gamification && typeof applyGamificationPayload === 'function') {
        applyGamificationPayload(claim.gamification, {
          emitXpToast: true,
          event: 'loot_find',
          awarded: Number(claim?.gamification?.awarded || 0),
        });
      }

      setLoot(lootItem);
      setLootLog(await loadLootLogFromServer());
    } catch (error) {
      setLoot(null);
      setLootError(getLootErrorMessage(error));
      if (error?.code === 'cooldown_active') {
        setCooldownId(location.id);
        setCooldownUntil(Date.now() + Number(error?.data?.retry_after || LOOT_COOLDOWN / 1000) * 1000);
        loadLootLogFromServer().then(setLootLog).catch(() => {});
      }
    } finally {
      releaseLootClaimLock(800);
    }
  }

  const formatTime = (secs) => {
    const mm = String(Math.floor(secs / 60)).padStart(2, '0');
    const ss = String(secs % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  let countdown = 0;
  if (cooldownId && cooldownUntil) {
    countdown = Math.max(0, cooldownUntil - Date.now());
  }

  return (
    <div className="step-tracker" style={{
      backgroundImage: `url(${bg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      {/* Kompass */}
      <div style={{
        position: "absolute",
        top: "19.5%",
        left: "3%",
        width: "54px",
        height: "54px",
        zIndex: 10
      }}>
        <img
          src={kompassRose}
          alt="Kompass Rose"
          style={{
            width: "54px",
            height: "54px",
            position: "absolute",
            left: 0,
            top: 0
          }}
        />
        <CompassNeedle
          rotation={rotation}
          imgSrc={kompassNadel}
          size={54}
        />
      </div>

      {/* Schrittzahl */}
      {/* Schrittzahl */}
      <div className="st-steps">
        {isTracking
          ? `${sessionSteps.toLocaleString()} Schritte`
          : `${globalSteps.toLocaleString()} Schritte`}
      </div>

      <div className="st-percent">
        {percent}% von {DAILY_STEP_GOAL}
      </div>
      <div className="st-bar">
        <div className="st-bar__fill" style={{ width: `${percent}%` }} />
      </div>
      <button
        className={`st-btn ${isTracking ? 'stop' : 'start'}`}
        onClick={isTracking ? stopTracking : startTracking}
      >
        {isTracking ? 'Schatzsuche stoppen' : 'Schatzsuche starten'}
      </button>


      {requiresMotionPermission && !isTracking && motionPermission !== 'granted' && (
        <div
          style={{
            margin: '0.75rem auto 0',
            maxWidth: 320,
            background: 'rgba(255,255,255,0.92)',
            color: '#5b3a00',
            borderRadius: 10,
            padding: '0.65rem 0.8rem',
            fontSize: '0.82rem',
            lineHeight: 1.35,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            textAlign: 'center',
          }}
        >
          Beim Start wird auf dem iPhone automatisch die Freigabe für Bewegungsdaten abgefragt.
        </div>
      )}

      {isTracking && !sensorReady && !trackingError && (
        <div
          style={{
            margin: '0.75rem auto 0',
            maxWidth: 320,
            background: 'rgba(255,255,255,0.92)',
            color: '#17436b',
            borderRadius: 10,
            padding: '0.65rem 0.8rem',
            fontSize: '0.82rem',
            lineHeight: 1.35,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            textAlign: 'center',
          }}
        >
          Bewegungssensor wird gestartet … bitte bewege dein Gerät ein paar Schritte.
        </div>
      )}

      {trackingError && (
        <div
          style={{
            margin: '0.75rem auto 0',
            maxWidth: 340,
            background: 'rgba(255,244,244,0.96)',
            color: '#8a1f1f',
            border: '1px solid rgba(138,31,31,0.18)',
            borderRadius: 10,
            padding: '0.75rem 0.9rem',
            fontSize: '0.82rem',
            lineHeight: 1.4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            textAlign: 'center',
          }}
        >
          {trackingError}
        </div>
      )}

      {/* Schatztruhe */}
      <button
        type="button"
        className="schatztruhe-button"
        aria-label="Schatzkiste looten"
        disabled={isLooting || !isHovernd}
        onClick={() => handleTruheClick(nearbyLocation)}
      >
        <img
          src={require('../assets/schatztruhe-klein.png')}
          alt=""
          className={`schatztruhe-icon${isHovernd ? ' hovernd' : ''}`}
        />
      </button>
      {isLooting && <div role="status">Loot wird geladen …</div>}
      {lootError && <div role="alert" className="loot-error">{lootError}</div>}

      {/* Fußbereich */}
      <div className="st-footer">
        <div className="st-footer__item">
          Zeit: {formatTime(elapsed)} Min.
        </div>
        <div className="st-footer__item">
          Serie: {streak} Tage in Folge
        </div>
        <div className="st-footer__item">
          Heute: {(dailyTotal + sessionSteps).toLocaleString()} Schritte
        </div>
        <div className="st-footer__item">
          Gesamt: {(allTimeTotal + sessionSteps).toLocaleString()} Schritte
        </div>
      </div>
      {goalReachedToday && (
        <div className="goal-message">🎉 Tagesziel erreicht!</div>
      )}

      {/* --- Fixes Overlay für Loot oder Cooldown --- */}
      {loot && (
        <div
          className="loot-popup"
          style={{
            position: 'fixed',
            top: '18vh',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: '#fffbe8',
            border: '2px solid #e0c875',
            borderRadius: 12,
            padding: '1.2em 2em',
            boxShadow: '0 4px 16px #d9be87',
            maxWidth: 300,
            textAlign: 'center',
            color: '#2f2305'
          }}
          onClick={() => setLoot(null)}
        >
          {loot.icon ? (
            <img src={loot.icon} alt={loot.name} style={{ width: 54, height: 54, marginBottom: 8 }} />
          ) : (
            <div style={{ fontSize: "2rem" }}>🎁</div>
          )}
          <div>Du hast gefunden:</div>
          <strong>{loot.name}</strong>
          <div style={{ color: '#6f4f00', marginTop: '0.5rem', fontWeight: 'bold' }}>{loot.rarity}</div>
          <div style={{ marginTop: "1rem", color: '#4a3710' }}>(Tippen zum Schließen)</div>
        </div>
      )}
      {!loot && cooldownId && countdown > 0 && (
        <div
          className="loot-popup"
          style={{
            position: 'fixed',
            top: '18vh',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: '#fffbe8',
            border: '2px solid #d13',
            borderRadius: 12,
            padding: '1.2em 2em',
            boxShadow: '0 4px 16px #d9be87',
            maxWidth: 300,
            textAlign: 'center',
            color: '#d13'
          }}
          onClick={() => { setCooldownId(null); setCooldownUntil(null); }}
        >
          <div style={{ fontSize: "2rem" }}>⏳</div>
          <div>
            Cooldown aktiv für:<br />
            <b style={{ color: '#a26d1d', fontSize: '1.15em' }}>
              {(allLocations.find(l => l.id === cooldownId)?.title) || "Unbekannter Ort"}
            </b>
          </div>
          <strong style={{ marginTop: '0.4em', display: 'block' }}>
            {formatCooldown(countdown)}
          </strong>
          <div style={{ marginTop: "1rem" }}>Tippe, um auszublenden.</div>
        </div>
      )}
    </div>
  );
}

export default StepTracker;
